/**
 * App-wide date helpers. Display and pickers use DD/MM/YYYY.
 * API / DB values stay ISO (YYYY-MM-DD).
 */

export function parseToDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) || value.getFullYear() < 1900 ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  // DD/MM/YYYY
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) || d.getFullYear() < 1900 ? null : d;
  }

  // YYYY-MM-DD (optionally with time)
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (year < 1900 || month < 1 || month > 12 || day < 1) return null;
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return null;
  return d;
}

export function toIsoDate(value) {
  const d = parseToDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDate(value, fallback = '-') {
  const d = parseToDate(value);
  if (!d) return fallback;
  const day = String(d.getDate()).padStart(2, '0');
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = d.getFullYear();
  return `${day}/${m}/${y}`;
}

export function formatDateTime(value, fallback = '-') {
  const d = parseToDate(value);
  if (!d) return fallback;
  const date = formatDate(d, '');
  if (!date) return fallback;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${date} ${hh}:${mm}`;
}

export function todayIso() {
  return toIsoDate(new Date());
}

/** Add calendar months; clamps to last day of the target month (e.g. 31 Jan → 28/29 Feb). */
export function addMonthsIso(value, months = 1) {
  const d = parseToDate(value) || new Date();
  const day = d.getDate();
  const last = new Date(d.getFullYear(), d.getMonth() + months + 1, 0).getDate();
  const next = new Date(d.getFullYear(), d.getMonth() + months, Math.min(day, last));
  return toIsoDate(next);
}
